import { Link } from "react-router-dom";

interface CTABlockProps {
  title: string;
  description?: string;
  primaryCta: { text: string; href: string };
  secondaryCta?: { text: string; href: string };
  variant?: "dark" | "accent";
  backgroundImage?: string;
  backgroundImageAlt?: string;
}

const variantStyles = {
  dark: {
    bg: "bg-secondary on-dark",
    title: "text-secondary-foreground",
    desc: "text-white/90",
    primary: "bg-accent text-accent-foreground hover:bg-accent/90",
    secondary:
      "border border-secondary-foreground/35 text-secondary-foreground hover:border-accent hover:text-accent",
  },
  accent: {
    bg: "bg-primary",
    title: "text-primary-foreground",
    desc: "text-primary-foreground/60",
    primary: "bg-accent text-accent-foreground hover:bg-accent/90",
    secondary: "border border-primary-foreground/30 text-primary-foreground hover:border-accent hover:text-accent",
  },
};

const CTABlock = ({
  title,
  description,
  primaryCta,
  secondaryCta,
  variant = "accent",
  backgroundImage,
  backgroundImageAlt = "",
}: CTABlockProps) => {
  const s = variantStyles[variant];

  return (
    <section className={`relative overflow-hidden ${s.bg} py-20 md:py-28`}>
      {backgroundImage && (
        <>
          <img
            src={backgroundImage}
            alt={backgroundImageAlt}
            className="absolute inset-0 w-full h-full object-cover"
            loading="lazy"
          />
          <div className="absolute inset-0 bg-secondary/84" />
          <div className="absolute inset-0 bg-gradient-to-r from-secondary/94 via-secondary/80 to-secondary/86" />
        </>
      )}
      <div className="relative mx-auto max-w-content px-5 md:px-12 text-center">
        <h2 className={`font-headline text-3xl sm:text-4xl md:text-5xl leading-tight mb-5 ${s.title}`}>
          {title}
        </h2>
        {description && (
          <p className={`${s.desc} text-base md:text-lg leading-relaxed max-w-xl mx-auto mb-10`}>
            {description}
          </p>
        )}
        <div className="flex flex-col sm:flex-row gap-4 justify-center">
          <Link
            to={primaryCta.href}
            className={`inline-block font-body text-sm uppercase tracking-[0.12em] px-10 py-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary ${s.primary}`}
          >
            {primaryCta.text}
          </Link>
          {secondaryCta && (
            <Link
              to={secondaryCta.href}
              className={`inline-block font-body text-sm uppercase tracking-[0.12em] px-10 py-4 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-accent/70 focus-visible:ring-offset-2 focus-visible:ring-offset-secondary ${s.secondary}`}
            >
              {secondaryCta.text}
            </Link>
          )}
        </div>
      </div>
    </section>
  );
};

export default CTABlock;
